import React, { useEffect, useState, useContext } from "react";
import { useTranslation } from "react-i18next";

import DeleteModal from "components/shared/DeleteModal";
import ItemGenerated from "components/shared/ItemGenerated";
import FormFooter from "shared/components/FormElements/FormFooter";

import Input from "shared/components/FormElements/Input";
import Button from "shared/components/FormElements/Button";
import NotFound from "shared/components/UIElements/NotFound";
import InputPhoneNumber from "components/ui/input/InputPhoneNumber";
import { getCurrentLanguage } from "shared/utility/lang";

import { VALIDATOR_REQUIRE, VALIDATOR_NONE } from "shared/util/validators";
import { PERMISSION_TYPES } from "@routebot/constants";

import { useForm } from "shared/hooks/form-hook";
import { useHttpClient } from "shared/hooks/http-hook";
import { StoreContext } from "shared/context/store-context";
import { AuthContext } from "shared/context/auth-context";

const InventoryAttendantItem = ({ itemId, onClose, isNewItem, gotoItem, DeleteItem, UpdateItem, InsertItem }) => {
  const auth = useContext(AuthContext);
  const { t } = useTranslation();
  const [isItemLoaded, setIsItemLoaded] = useState(false);
  const [loadedItem, setLoadedItem] = useState();
  const [generatedItemId, setGeneratedItemId] = useState();
  const { isLoading, error, sendRequest } = useHttpClient();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const { attendants, vehicles } = useContext(StoreContext);

  // Phone number state variables — default country from logged-in client's countryCode
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState(() => (auth?.countryCode || "TR").toUpperCase());
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(true);

  const itemType = "attendant";
  const endPointUpdate = "attendants";
  const endPointDelete = "attendants";
  const endPointCreate = "attendants";

  // Phone number change handler
  const phoneNumberChangeHandler = (phoneNumber, countryCode, isValid) => {
    setPhoneNumber(phoneNumber || "");
    setCountryCode(countryCode);
    setIsPhoneNumberValid(isValid);
  };

  // Initialize form with all fields from the schema
  const [formState, inputHandler, setFormData] = useForm(
    {
      name: { value: "", isValid: false },
      identityNumber: { value: "", isValid: false },
      sgkRegistration: { value: "", isValid: true },
      institution1: { value: "", isValid: true },
      institution2: { value: "", isValid: true },
      institution3: { value: "", isValid: true },
      notes: { value: "", isValid: true },
    },
    false
  );

  // Fetching item data
  useEffect(() => {
    const fetchItem = async () => {
      if (!isNewItem) {
        setIsItemLoaded(false);
        try {
          const currentAttendant = attendants.find((attendant) => attendant.id === itemId);
          if (currentAttendant) {
            setLoadedItem(currentAttendant);
            // Initialize phone number state
            setPhoneNumber(currentAttendant.phone || "");
            setCountryCode((auth?.countryCode || "TR").toUpperCase());
            setIsPhoneNumberValid(true);
          }
        } catch (err) {
          console.log(err);
        }
        setIsItemLoaded(true);
      } else {
        setIsItemLoaded(true);
      }
    };
    fetchItem();
  }, [itemId, attendants, isNewItem, setFormData]);

  const itemUpdateSubmitHandler = async (event) => {
    event.preventDefault();
    const endpoint = isNewItem
      ? `${process.env.REACT_APP_BACKEND_URL}/${endPointCreate}`
      : `${process.env.REACT_APP_BACKEND_URL}/${endPointUpdate}/${itemId}`;

    const postType = isNewItem ? "POST" : "PATCH";

    // Build the object to send
    const infoToBeSent = {
      name: formState.inputs.name.value,
      phone: phoneNumber,
      identityNumber: formState.inputs.identityNumber.value,
      sgkRegistration: formState.inputs.sgkRegistration.value,
      institution1: formState.inputs.institution1.value,
      institution2: formState.inputs.institution2.value,
      institution3: formState.inputs.institution3.value,
      notes: formState.inputs.notes.value,
    };

    try {
      const responseData = await sendRequest(endpoint, postType, JSON.stringify(infoToBeSent));

      if (responseData?.[itemType]) {
        if (isNewItem) {
          InsertItem(responseData[itemType]);
          setGeneratedItemId(responseData[itemType].id);
        } else {
          UpdateItem(responseData[itemType]);
          window.toast.success(t("updateSuccess"));
        }
      }
    } catch (err) {
      window.toast.error(t("httpCodes." + err.code));
    }
  };

  if (generatedItemId) {
    return (
      <ItemGenerated
        itemType={itemType}
        onGotoNewItem={() => gotoItem(generatedItemId)}
        onCreateNewItem={() => setGeneratedItemId(null)}
        onClose={() => {
          setGeneratedItemId(false);
          onClose();
        }}
      />
    );
  }

  if (!isItemLoaded && !isNewItem) {
    return <p>Loading...</p>;
  }

  if (!isNewItem && isItemLoaded && !loadedItem && !error) {
    return <NotFound item={itemType} />;
  }

  return (
    <React.Fragment>
      <DeleteModal
        endPoint={endPointDelete}
        itemType={itemType}
        itemId={itemId}
        onDeleted={() => {
          DeleteItem(itemId);
          onClose();
        }}
        onClose={() => setShowConfirmModal(false)}
        showModal={showConfirmModal}
      />

      {isItemLoaded && (
        <form className="form-container" onSubmit={itemUpdateSubmitHandler}>
          <Input
            id="name"
            element="input"
            type="text"
            label={t("inventoryAttendant.fields.name")}
            validators={[VALIDATOR_REQUIRE()]}
            errorText={t("inventoryAttendant.errors.nameRequired")}
            onInput={inputHandler}
            initialValue={loadedItem?.name || ""}
            initialValid={isNewItem ? false : true}
          />

          <InputPhoneNumber
            id="phone"
            label={t("inventoryAttendant.fields.phone")}
            phoneNumber={phoneNumber}
            countryCode={countryCode}
            onChange={phoneNumberChangeHandler}
            errorText={t("inventoryAttendant.errors.invalidPhone")}
            language={getCurrentLanguage()}
            hideLabel={auth.countryCode === countryCode}
          />

          {!isNewItem && (
            <Input
              id="vehicle"
              element="input"
              type="text"
              label={t("inventoryAttendant.fields.currentVehicle")}
              validators={[VALIDATOR_NONE()]}
              disabled={true}
              initialValue={vehicles.find((vehicle) => vehicle.attendant === loadedItem?.id)?.plate || "-"}
              initialValid={true}
              onInput={inputHandler}
            />
          )}

          <Input
            id="identityNumber"
            element="input"
            type="text"
            label={t("inventoryAttendant.fields.identityNumber")}
            validators={[VALIDATOR_REQUIRE()]}
            errorText={t("inventoryAttendant.errors.identityRequired")}
            onInput={inputHandler}
            initialValue={loadedItem?.identityNumber || ""}
            initialValid={isNewItem ? false : true}
          />

          {!isNewItem && loadedItem.googleDriveFolderId && (
            <div style={{ marginTop: "1rem", marginBottom: "1rem" }}>
              <div className="horizontal-flex" style={{ alignItems: "center", justifyContent: "flex-start" }}>
                <div>
                  <Button
                    type="button"
                    href={`https://drive.google.com/drive/folders/${loadedItem.googleDriveFolderId}`}
                  >
                    {t("inventoryCommon.goToFiles")}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <Input
            id="sgkRegistration"
            element="input"
            type="text"
            label={t("inventoryAttendant.fields.sgkRegistration")}
            validators={[VALIDATOR_NONE()]}
            onInput={inputHandler}
            initialValue={loadedItem?.sgkRegistration || ""}
            initialValid={true}
          />

          <Input
            id="institution1"
            element="input"
            type="text"
            label={t("inventoryAttendant.fields.institution1")}
            validators={[VALIDATOR_NONE()]}
            onInput={inputHandler}
            initialValue={loadedItem?.institution1 || ""}
            initialValid={true}
          />

          <Input
            id="institution2"
            element="input"
            type="text"
            label={t("inventoryAttendant.fields.institution2")}
            validators={[VALIDATOR_NONE()]}
            onInput={inputHandler}
            initialValue={loadedItem?.institution2 || ""}
            initialValid={true}
          />

          <Input
            id="institution3"
            element="input"
            type="text"
            label={t("inventoryAttendant.fields.institution3")}
            validators={[VALIDATOR_NONE()]}
            onInput={inputHandler}
            initialValue={loadedItem?.institution3 || ""}
            initialValid={true}
          />

          <Input
            id="notes"
            element="textarea"
            type="text"
            label={t("inventoryAttendant.fields.notes")}
            rows={7}
            validators={[VALIDATOR_NONE()]}
            errorText=""
            onInput={inputHandler}
            initialValue={loadedItem?.notes || ""}
            initialValid={true}
          />

          {auth.permissions[PERMISSION_TYPES.ACCESS_TO_WRITE_INVENTORY] && (
            <FormFooter
              isNewItem={isNewItem}
              onUpdate={itemUpdateSubmitHandler}
              onDelete={() => setShowConfirmModal(true)}
              isUpdating={isLoading}
              disabled={!formState.isValid || !formState.inputs.name.value || !isPhoneNumberValid || isLoading}
            />
          )}
        </form>
      )}
    </React.Fragment>
  );
};

export default InventoryAttendantItem;
